/**
 * The connector settings that decide whether a credential travels to the right server, over a
 * checked channel (round 8 review, owner rule: no compromise on credentials). An AI app that
 * switched certificate checking off, or pinned a host key of its own, would let a man in the middle
 * read the credential the person types on `/connections/:id/setup`. So over MCP:
 *
 * - a `flag` is forced to its `safe` value at create, and an AI-supplied value that differs is
 *   refused (the field id only);
 * - a `trust_anchor` (a pinned fingerprint) is never taken from an AI app: the person pastes it.
 *
 * A person can still change all of them in the editor as before. `transportSecurity.test.ts` walks
 * every connector seed and fails on a TLS, certificate, host-key or verification field that is not
 * decided here, so a new connector cannot add one silently.
 */

export interface TransportSecurityField {
  id: string;
  kind: "flag" | "trust_anchor";
  /** For a `flag`: the value that keeps the channel checked. */
  safe?: boolean;
  /** What the setup page says about the field. */
  label: string;
}

export const TRANSPORT_SECURITY_FIELDS: readonly TransportSecurityField[] = [
  {
    id: "trustServerCertificate",
    kind: "flag",
    safe: false,
    label: "Trust the server certificate without checking it",
  },
  { id: "strictHostKeyChecking", kind: "flag", safe: true, label: "Strict host-key checking" },
  { id: "hostKeyFingerprint", kind: "trust_anchor", label: "Pinned host-key fingerprint" },
  { id: "acceptedFingerprints", kind: "trust_anchor", label: "Accepted fingerprints" },
  // The SSH algorithm allow-list: an AI app could re-enable weak ciphers or key exchanges.
  { id: "disabledAlgorithms", kind: "trust_anchor", label: "SSH algorithm overrides" },
];

const BY_ID = new Map(TRANSPORT_SECURITY_FIELDS.map((field) => [field.id, field]));

export function transportSecurityField(id: string): TransportSecurityField | undefined {
  return BY_ID.get(id);
}

/** The ids a connector's field definitions declare from the list, in the definition's order. */
export function declaredTransportFields(fieldDefinitions: unknown): TransportSecurityField[] {
  if (!Array.isArray(fieldDefinitions)) return [];
  const found: TransportSecurityField[] = [];
  for (const entry of fieldDefinitions) {
    const id = (entry as { id?: unknown } | null)?.id;
    const field = typeof id === "string" ? BY_ID.get(id) : undefined;
    if (field) found.push(field);
  }
  return found;
}

function asBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lower = value.trim().toLowerCase();
    if (lower === "true") return true;
    if (lower === "false" || lower === "") return false;
  }
  return null;
}

/** Whether a value an AI app supplied is anything but the safe one (a trust anchor: any value). */
export function weakensTransportSecurity(field: TransportSecurityField, value: unknown): boolean {
  if (field.kind === "trust_anchor") {
    if (value === undefined || value === null) return false;
    const text = String(value).trim();
    return text !== "" && text !== "[]" && text !== "{}";
  }
  if (value === undefined || value === null) return false;
  return asBoolean(value) !== field.safe;
}

/** The text the setup page shows for a stored value, and whether it is the weak one. */
export function transportSecurityState(
  field: TransportSecurityField,
  value: unknown,
): { text: string; weak: boolean } {
  if (field.kind === "trust_anchor") {
    const present =
      value !== undefined &&
      value !== null &&
      String(value).trim() !== "" &&
      String(value).trim() !== "[]" &&
      String(value).trim() !== "{}";
    return { text: present ? "Set" : "Not set", weak: false };
  }
  // An unreadable value is shown as the weak one (fails closed).
  const parsed = asBoolean(value);
  const on = parsed ?? !field.safe;
  const safe = on === field.safe;
  if (field.id === "trustServerCertificate")
    return { text: on ? "Yes, not checked" : "No, checked", weak: !safe };
  return { text: on ? "On" : "Off, the server's identity is not verified", weak: !safe };
}
